import React, { useEffect, useState } from "react";
import { getSettings, updateSettings, sageStatus, sageToggle, verifactuChain } from "@/lib/api";
import { useApp } from "@/lib/AppContext";
import { toast } from "sonner";
import { ShieldCheck, Settings as SettingsIcon, Database, Receipt, Save, Link2 } from "lucide-react";

export default function Settings() {
  const { refreshSage, refreshChain } = useApp();
  const [s, setS] = useState(null);
  const [sage, setSage] = useState(null);
  const [chain, setChain] = useState(null);
  const [draft, setDraft] = useState({});

  useEffect(() => {
    Promise.all([getSettings(), sageStatus(), verifactuChain()]).then(([a, b, c]) => {
      setS(a); setSage(b); setChain(c);
      setDraft(a);
    });
  }, []);

  const save = async () => {
    const u = await updateSettings(draft);
    setS(u);
    refreshChain();
    refreshSage();
    toast.success("Ajustes guardados");
  };

  const toggleSage = async () => {
    const r = await sageToggle();
    setSage(await sageStatus());
    refreshSage();
    toast.message(`Sage ahora ${r.online ? "ONLINE" : "OFFLINE"}`);
  };

  if (!s) return <div className="p-8 text-center text-slate-400">Cargando ajustes…</div>;

  return (
    <div className="h-full overflow-y-auto thin-scroll p-6 bg-slate-50">
      <div className="max-w-4xl mx-auto space-y-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-slate-500">Configuración</div>
          <h1 className="font-display text-3xl font-semibold text-slate-900 flex items-center gap-2">
            <SettingsIcon size={26} /> Ajustes del TPV
          </h1>
        </div>

        {/* Verifactu */}
        <Card icon={ShieldCheck} title="Verifactu" subtitle="Modalidad y datos del emisor">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Modalidad</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {["VERIFACTU", "NO_VERIFACTU"].map((m) => (
                  <button
                    key={m}
                    onClick={() => setDraft({ ...draft, verifactu_mode: m })}
                    className={`h-11 px-3 text-sm font-medium border rounded-md ${
                      draft.verifactu_mode === m
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white border-slate-300 hover:border-slate-500"
                    }`}
                  >{m === "VERIFACTU" ? "VERI*FACTU · Envío inmediato AEAT" : "NO VERI*FACTU · Custodia local XAdES"}</button>
                ))}
              </div>
              <p className="mt-2 text-xs text-slate-500">
                {draft.verifactu_mode === "VERIFACTU"
                  ? "Cada factura se envía a la AEAT al emitirse; el ticket muestra la mención VERI*FACTU + QR de cotejo."
                  : "Las facturas se firman con XAdES localmente y se conservan 4 años; el ticket NO lleva mención VERI*FACTU."}
              </p>
            </div>
            <div className="space-y-3">
              <div>
                <Label>NIF emisor</Label>
                <Input value={draft.emisor_nif || ""} onChange={(v) => setDraft({ ...draft, emisor_nif: v })} />
              </div>
              <div>
                <Label>Nombre fiscal</Label>
                <Input value={draft.emisor_nombre || ""} onChange={(v) => setDraft({ ...draft, emisor_nombre: v })} />
              </div>
              <div>
                <Label>Domicilio</Label>
                <Input value={draft.emisor_direccion || ""} onChange={(v) => setDraft({ ...draft, emisor_direccion: v })} />
              </div>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button onClick={save} className="h-10 px-4 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 flex items-center gap-2">
              <Save size={14} /> Guardar
            </button>
          </div>
        </Card>

        {/* Verifactu chain status */}
        <Card icon={Link2} title="Cadena de huellas" subtitle="Integridad y trazabilidad">
          <div className="grid grid-cols-3 gap-3 text-sm">
            <KV k="Total registros" v={chain?.total_records} />
            <KV k="Integridad" v={chain?.valid ? "✓ Íntegra" : "✗ Rota"} tone={chain?.valid ? "ok" : "err"} />
            <KV k="Última huella" v={(chain?.last_hash || "").slice(0, 14) + "…"} mono />
          </div>
        </Card>

        {/* Sage status */}
        <Card icon={Database} title="Sage 200c" subtitle="Estado del conector ERP (mock)">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <KV k="Estado" v={sage?.online ? "Online" : "Offline"} tone={sage?.online ? "ok" : "warn"} />
            <KV k="Cola pendiente" v={sage?.pending_queue} />
            <KV k="Versión" v={sage?.version} />
          </div>
          <div className="mt-3 flex gap-2">
            <button onClick={toggleSage} className="h-10 px-4 text-sm border border-slate-300 bg-white hover:bg-slate-50 rounded-md">
              Toggle estado (demo)
            </button>
          </div>
          <p className="mt-3 text-xs text-slate-500">
            En producción el TPV envía albaranes a Sage 200c vía API; Sage actúa como SIF certificado y genera el
            registro Verifactu. <b>Nunca</b> se escribe directamente en SQL: rompería la cadena de certificación.
          </p>
        </Card>

        {/* Receipt template preview */}
        <Card icon={Receipt} title="Ticket / Factura" subtitle="Numeración y plantilla">
          <div className="text-[11px] text-slate-500">
            Series activas: <b>T</b> (tickets) · <b>A</b> (abonos). Máscara: <code>{"{SERIE}/{NUMERO}"}</code>. Tipos Verifactu: F1 / F2 / R5.
          </div>
        </Card>
      </div>
    </div>
  );
}

function Card({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-9 w-9 rounded-md bg-slate-100 text-slate-700 flex items-center justify-center">
          <Icon size={16} />
        </div>
        <div>
          <div className="font-display text-base font-semibold text-slate-900">{title}</div>
          <div className="text-[11px] text-slate-500">{subtitle}</div>
        </div>
      </div>
      {children}
    </div>
  );
}

function Label({ children }) {
  return <label className="text-xs uppercase tracking-wider text-slate-500">{children}</label>;
}
function Input({ value, onChange }) {
  return <input value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm mt-1" />;
}
function KV({ k, v, mono, tone }) {
  const toneCls = tone === "ok" ? "text-emerald-700" : tone === "err" ? "text-red-700" : tone === "warn" ? "text-amber-700" : "text-slate-900";
  return (
    <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{k}</div>
      <div className={`mt-1 font-display text-lg font-semibold ${mono ? "font-mono text-sm" : ""} ${toneCls}`}>{v ?? "—"}</div>
    </div>
  );
}
